import { Button, Radio, Typography } from 'antd';
import { ArrowLeftOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import TabletHeader from '../../../components/Operations/TabletHeader';
import SawCutQueueTable from '../../../components/Operations/SawCutQueue/SawCutQueueTable/index.tsx';
import ProductionMethodIndicator from '../../../components/Operations/ProductionMethodIndicator';
import { QueueSummary } from '../../../components/Operations/SawCutQueue/types';
import {
	DEFAULT_PRIORITIZATION,
	DEFAULT_QUEUE_VIEW,
} from '../../../components/Operations/SawCutQueue/queueClassification';
import { useCallback, useState } from 'react';
import { useBuildingContext } from '../../../context/BuildingContext';
import {
	productionMethodParam,
	usePersistedProductionMethod,
} from '../../../hooks/usePersistedProductionMethod';
import {
	TABLET_BACK_BUTTON_STYLE,
	TABLET_SEARCH_STYLE,
	TABLET_RADIO_GROUP_STYLE,
	TABLET_FILTER_BUTTON_STYLE,
	TABLET_FILTER_ROW_STYLE,
} from '../../../components/Operations/tabletStyles';

const SawCutQueue = () => {
	const { buildingId } = useBuildingContext();
	// The station's method is a setting, not a filter, so it is not in the URL:
	// a link to this queue should not be able to carry a method that outlives the
	// tap that followed it.
	const { scope: productionMethodScope, setScope: setProductionMethodScope } =
		usePersistedProductionMethod();
	const productionMethod = productionMethodParam(productionMethodScope);
	const queryParams = new URLSearchParams(location.search);
	const materialCutStatus = queryParams.get('group_cut_status') || 'Pending';
	const queueView = queryParams.get('queue_view') || DEFAULT_QUEUE_VIEW;
	const prioritization =
		queryParams.get('prioritization') || DEFAULT_PRIORITIZATION;
	// An explicit `ordering` is the desktop table's column sort, and the API lets
	// it win over readiness. Carried into this URL — a shared link, a back
	// button — it would leave "Production first" lit over a list the server put
	// in another order, so the control shows the order in force rather than the
	// one that was asked for.
	const ordering = queryParams.get('ordering');
	const effectivePrioritization = ordering ? 'due_date' : prioritization;
	const navigate = useNavigate();
	const [isReady, setIsReady] = useState(false);
	const [summary, setSummary] = useState<QueueSummary | undefined>();

	const setParam = (name: string, value: string) => {
		const params = new URLSearchParams(location.search);
		params.set(name, value);
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	// Choosing an order here drops any column sort riding in the URL, so the
	// tap does what the button says instead of being overridden by a parameter
	// this screen has no control for.
	const setPrioritization = (value: string) => {
		const params = new URLSearchParams(location.search);
		params.set('prioritization', value);
		params.delete('ordering');
		params.set('page', '1');
		navigate({ search: params.toString() });
	};

	const onSummaryChange = useCallback((next?: QueueSummary) => {
		if (next) setSummary(next);
	}, []);

	// Whether the cut is still to be made, not whether the job is finished. The
	// wording is the operator's, and it is why a shipped job with an open cut is
	// under Pending.
	const cutStatusOptions = ['Pending', 'Completed', 'All'];

	// All leads because it is what the queue opens on: Production asked to see
	// every class in one ranked list and filter down from there. The counts
	// describe the whole queue for the current filters, so a view label keeps its
	// number even while that view is the one on screen.
	const totalClassified = summary
		? summary.continue_cutting +
		  summary.start_machine_soon +
		  summary.standard_ready +
		  summary.blocked +
		  summary.needs_review +
		  summary.completed
		: undefined;
	const viewOptions: { value: string; label: string; count?: number }[] = [
		{ value: 'All', label: 'All', count: totalClassified },
		{ value: 'Actionable', label: 'Ready', count: summary?.actionable },
		{ value: 'Blocked', label: 'Blocked', count: summary?.blocked },
		{
			value: 'NeedsReview',
			label: 'Needs review',
			count: summary?.needs_review,
		},
	];

	// Both orders are named, because a switch labelled only "Production first"
	// leaves its off state unlabelled: the operator can see the ordering is not
	// production-first without being told what it now is. As with the views, the
	// order the queue opens on leads.
	const prioritizationOptions = [
		{ value: 'readiness', label: 'Production first' },
		{ value: 'due_date', label: 'Due date' },
	];

	return (
		<main
			data-testid="page-fastlane-saw-cut-queue"
			data-ready={isReady ? 'true' : 'false'}
			className="flex flex-col w-full p-3 saw-cut-queue-page"
		>
			{/*
			 * No title: the Shop Floor App already frames this page in a tab named
			 * "Saw Cut Queue", and on a 10.1" tablet the repeated heading costs a
			 * row of the list to say something the operator just tapped.
			 *
			 * The method sits up here with the building because the two read the
			 * same way: what this station is, set once, not touched while cutting.
			 * The cut-status buttons went the other way, down to the filter row, so
			 * that every choice an operator does make is on one line.
			 */}
			<TabletHeader
				showOnClickButton
				icon={<ArrowLeftOutlined />}
				buttonText="Back"
				actionButtonStyle={TABLET_BACK_BUTTON_STYLE}
				searchStyle={TABLET_SEARCH_STYLE}
				searchPlaceholder="Bar ID, SO, or part"
				handleOnClick={() => navigate('/tablet/operations')}
				secondaryAction={
					// The saw is where an extra gets cut, so the entry point belongs
					// here rather than only on the Operations home the operator has
					// already left. `from=cutting` sends Back to this queue.
					<Button
						type="primary"
						size="large"
						icon={<PlusOutlined />}
						style={TABLET_BACK_BUTTON_STYLE}
						onClick={() =>
							navigate('/tablet/operations/reservations?from=cutting')
						}
					>
						Reserve Material
					</Button>
				}
				stationExtra={
					<ProductionMethodIndicator
						scope={productionMethodScope}
						onChange={(next) => {
							setProductionMethodScope(next);
							// The list under the new method is a different list, so the page
							// the operator was on does not describe anything in it.
							setParam('page', '1');
						}}
					/>
				}
				onSearch={(value) => {
					queryParams.set('search', String(value));
					if (value) {
						queryParams.set('group_cut_status', 'All');
					}
					queryParams.set('page', '1');
					navigate({ search: queryParams.toString() });
				}}
			/>

			{/*
			 * Every choice an operator makes while cutting, on one line: which cuts,
			 * which of them, in what order. It was a Divider plus two 60px rows plus
			 * the cut-status buttons up in the header, and on a 10.1" tablet inside
			 * the Shop Floor App frame that left a sliver of list.
			 *
			 * The three stay separate controls because they answer different
			 * questions, and folding them into one menu would make an order look like
			 * another thing to filter by. The cut status is labelled for a narrower
			 * reason: it and the class filter each have an "All", and side by side
			 * with nothing to tell them apart the two would read as the same button
			 * twice.
			 */}
			<div style={TABLET_FILTER_ROW_STYLE}>
				<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<Typography.Text type="secondary" style={{ fontSize: 15 }}>
						Cuts
					</Typography.Text>
					<Radio.Group
						value={materialCutStatus}
						optionType="button"
						buttonStyle="solid"
						size="large"
						style={TABLET_RADIO_GROUP_STYLE}
						data-testid="fastlane-cut-group-status"
						onChange={(e) => setParam('group_cut_status', String(e.target.value))}
					>
						{cutStatusOptions.map((option) => (
							<Radio.Button
								key={option}
								value={option}
								style={TABLET_FILTER_BUTTON_STYLE}
								data-testid={`fastlane-cut-group-status-${option}`}
							>
								{option}
							</Radio.Button>
						))}
					</Radio.Group>
				</span>

				<Radio.Group
					value={queueView}
					optionType="button"
					buttonStyle="solid"
					size="large"
					style={TABLET_RADIO_GROUP_STYLE}
					onChange={(e) => setParam('queue_view', String(e.target.value))}
				>
					{viewOptions.map((option) => (
						<Radio.Button
							key={option.value}
							value={option.value}
							style={TABLET_FILTER_BUTTON_STYLE}
							data-testid={`fastlane-cut-queue-view-${option.value}`}
						>
							{option.count === undefined
								? option.label
								: `${option.label} (${option.count})`}
						</Radio.Button>
					))}
				</Radio.Group>

				{/*
				 * Labelled, unlike the class filter. Sitting on the same row in the
				 * same button style, "Production first" would otherwise read as
				 * another class to filter by rather than as the order of the list.
				 *
				 * What each order does is in the tablet user guide at §12.3, not in a
				 * hint here: a hover title on a group of buttons cannot be read on a
				 * touch screen, because the tap that would open it changes the order
				 * instead.
				 */}
				<span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
					<Typography.Text type="secondary" style={{ fontSize: 15 }}>
						Order
					</Typography.Text>
					<Radio.Group
						value={effectivePrioritization}
						optionType="button"
						buttonStyle="solid"
						size="large"
						style={TABLET_RADIO_GROUP_STYLE}
						data-testid="fastlane-cut-prioritization"
						onChange={(e) => setPrioritization(String(e.target.value))}
					>
						{prioritizationOptions.map((option) => (
							<Radio.Button
								key={option.value}
								value={option.value}
								style={TABLET_FILTER_BUTTON_STYLE}
								data-testid={`fastlane-cut-prioritization-${option.value}`}
							>
								{option.label}
							</Radio.Button>
						))}
					</Radio.Group>
				</span>
			</div>

			<div style={{ display: 'flex', flexDirection: 'column' }}>
				<SawCutQueueTable
					key={buildingId}
					productionMethod={productionMethod}
					buildingId={buildingId}
					onSummaryChange={onSummaryChange}
					onReadyChange={(ready) => {
						if (ready) {
							setIsReady(true);
						}
					}}
				/>
			</div>
		</main>
	);
};

export default SawCutQueue;
